import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { combineLatest, filter, map, merge, of, switchMap, tap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { ApiService } from '../../../core/services/api.service';
import { StoreContextService } from '../../../core/services/store-context.service';
import { StoreRealtimeService } from '../../../core/services/store-realtime.service';
import { NetworkStatusService } from '../../../core/offline/network-status.service';
import { HubRoutingService } from '../../../core/offline/hub-routing.service';
import { OfflineSessionGuardService } from '../../../core/offline/offline-session-guard.service';
import { StorageKey, StorageService } from '../../../core/services/storage.service';
import { ToastService } from '../../../core/services/toast.service';
import { StoreDatePipe } from '../../../shared/pipes/store-date.pipe';
import { PosStateService } from '../pos-state.service';
import { isPosOperatorScreen } from '../shared/pos-operator-screen';

/** PRNALERT-1 — a printer past the store's "not responding" threshold (backend PosPrinterAlertDto). */
export interface PosPrinterAlert {
  id: string;
  printerId: string;
  printerName: string;
  connectionType: string;
  targetIdentifier: string;
  sinceUtc: string;
  lastFailureReason: string | null;
}

/** PRNHUB-1 — print jobs no device has collected past the store's timer (backend PosPrintHubAlertDto). */
export interface PosPrintHubAlert {
  id: string;
  sinceUtc: string;
  waitingCount: number;
  printerNames: string | null;
  deviceName: string | null;
  deviceLastSeenUtc: string | null;
}

export interface PosPrinterAlerts {
  enabled: boolean;
  remindLaterMinutes: number;
  alerts: PosPrinterAlert[];
  /** PRNHUB-1 — absent from a backend older than the print hub alerts. */
  hubEnabled?: boolean;
  hubAlerts?: PosPrintHubAlert[];
}

/** This terminal's answer to one alert: dismissed for the outage, or snoozed until a time (epoch ms). */
export interface PrinterAlertAnswer {
  dismissed?: boolean;
  remindAt?: number;
}

export type PrinterAlertAnswers = Record<string, PrinterAlertAnswer>;

/** The cloud's hint that the alert list changed (printing.printer-alerts job, settings save). */
export const PRINTER_ALERT_CHANGED = 'printer.alert.changed';

/**
 * PRNALERT-1 — the list is read when the store channel says it changed; this poll
 * only covers a missed hint (AppSync delivers at most once) and brings a snoozed
 * alert back on time. A minute is well inside any "remind me later".
 */
export const PRINTER_ALERT_POLL_MS = 60_000;

/** How long the "back online" toast stays up. */
export const PRINTER_BACK_ONLINE_TOAST_MS = 8000;

const HINTS = new Set([PRINTER_ALERT_CHANGED, 'poll.refresh']);

/**
 * PRNHUB-1 — the print device's heartbeat is this recent: it is on, so the jobs are stuck on
 * it rather than it being off (the edge app checks in every 30 s; the cloud writes it once a minute).
 */
export const PRINT_DEVICE_SEEN_FRESH_MS = 3 * 60_000;

/** Whether the device expected to collect the jobs was in touch recently. */
export function printDeviceOnline(alert: PosPrintHubAlert, now: number): boolean {
  if (!alert.deviceLastSeenUtc) return false;
  const seen = Date.parse(alert.deviceLastSeenUtc);
  return Number.isFinite(seen) && now - seen <= PRINT_DEVICE_SEEN_FRESH_MS;
}

/** The alerts this terminal should show now: not dismissed, and not snoozed past `now`. */
export function dueAlerts<T extends { id: string }>(alerts: T[], answers: PrinterAlertAnswers, now: number): T[] {
  return alerts.filter(a => {
    const answer = answers[a.id];
    if (!answer) return true;
    if (answer.dismissed) return false;
    return answer.remindAt == null || answer.remindAt <= now;
  });
}

/** Only the answers to alerts that are still open, so the stored map never grows. */
export function pruneAnswers(answers: PrinterAlertAnswers, liveIds: Iterable<string>): PrinterAlertAnswers {
  const live = new Set(liveIds);
  const kept: PrinterAlertAnswers = {};
  for (const [id, answer] of Object.entries(answers)) {
    if (live.has(id)) kept[id] = answer;
  }
  return kept;
}

/**
 * PRNALERT-1 — "Printer not responding" on every POS operator screen.
 *
 * The cloud raises an alert when a printer at this store has reported only
 * connection failures, and no successful print, for the store's threshold
 * (Print settings → Printer alerts, with the POS pop-up switched on). This
 * dialog needs an answer: "Remind me later" brings it back on THIS terminal
 * after the store's remind-later time, "Dismiss" hides it on this terminal
 * until the outage ends. Each answer is also sent to the cloud for the
 * Printer health history. When a printer it had shown is back, a toast says so.
 *
 * Root-mounted like the caller-ID card, so it reaches the floor, the order
 * screen and the settle screen; never over the lock screen (it waits for the
 * unlock), the PIN pad, the KDS or the displays, and never while the hub
 * serves this terminal — the alert list lives only in the cloud.
 *
 * PRNHUB-1 — the same dialog carries "Print jobs are not being collected":
 * jobs no device has picked for the store's timer (Print settings → Printer
 * alerts → Print hub). The same two buttons answer every alert shown.
 */
@Component({
  selector: 'app-printer-alert-dialog',
  standalone: true,
  imports: [CommonModule, TranslocoModule, StoreDatePipe],
  template: `
    <div class="pad-overlay" *ngIf="visible && (due.length > 0 || dueHubs.length > 0)">
      <div class="pad-dialog" role="alertdialog" aria-modal="true"
           aria-labelledby="printer-alert-title" aria-describedby="printer-alert-body">
        <div class="pad-icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h3 id="printer-alert-title">
          {{ dueHubs.length > 0
              ? ('pos.printerAlert.hubTitle' | transloco)
              : (due.length === 1 ? ('pos.printerAlert.titleOne' | transloco) : ('pos.printerAlert.titleMany' | transloco: { count: due.length })) }}
        </h3>

        <ng-container *ngIf="dueHubs.length > 0">
          <p id="printer-alert-body" class="pad-body">{{ 'pos.printerAlert.hubBody' | transloco }}</p>
          <ul class="pad-list">
            <li *ngFor="let h of dueHubs" class="pad-hub">
              <div class="pad-printer"><strong>{{ 'pos.printerAlert.hubWaiting' | transloco: { count: h.waitingCount } }}</strong></div>
              <div class="pad-since">{{ 'pos.printerAlert.since' | transloco: { time: (h.sinceUtc | storeDate:'timeShort') } }}</div>
              <div class="pad-reason" *ngIf="h.printerNames">{{ h.printerNames }}</div>
              <div class="pad-reason" *ngIf="h.deviceName">
                {{ h.deviceLastSeenUtc
                    ? ('pos.printerAlert.hubDevice' | transloco: { device: h.deviceName, time: (h.deviceLastSeenUtc | storeDate:'timeShort') })
                    : ('pos.printerAlert.hubDeviceNever' | transloco: { device: h.deviceName }) }}
              </div>
            </li>
          </ul>
          <p class="pad-next">{{ (hubDeviceOnline ? 'pos.printerAlert.hubNextStepOnline' : 'pos.printerAlert.hubNextStepOffline') | transloco }}</p>
        </ng-container>

        <ng-container *ngIf="due.length > 0">
          <p [attr.id]="dueHubs.length > 0 ? null : 'printer-alert-body'" class="pad-body">{{ 'pos.printerAlert.body' | transloco }}</p>
          <ul class="pad-list">
            <li *ngFor="let a of due">
              <div class="pad-printer"><strong>{{ a.printerName }}</strong> <span dir="ltr">{{ a.targetIdentifier }}</span></div>
              <div class="pad-since">{{ 'pos.printerAlert.since' | transloco: { time: (a.sinceUtc | storeDate:'timeShort') } }}</div>
              <div class="pad-reason" *ngIf="a.lastFailureReason" dir="ltr">{{ a.lastFailureReason }}</div>
            </li>
          </ul>
          <p class="pad-next">{{ 'pos.printerAlert.nextStep' | transloco }}</p>
        </ng-container>
        <div class="pad-actions">
          <button type="button" class="pad-btn" (click)="remindLater()">{{ 'pos.printerAlert.remindLater' | transloco }}</button>
          <button type="button" class="pad-btn pad-btn-primary" (click)="dismiss()">{{ 'pos.printerAlert.dismiss' | transloco }}</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }
    /* Above the caller-ID card (1400), below the lock overlay (--z-lock 10100): it waits for the unlock. */
    .pad-overlay { position: fixed; inset: 0; z-index: 1500; display: flex; align-items: center; justify-content: center;
      padding: 16px; background: rgba(0, 0, 0, 0.45); }
    .pad-dialog { width: 100%; max-width: 480px; max-height: calc(100vh - 32px); overflow-y: auto; padding: 24px;
      border-radius: 12px; background: var(--color-bg-surface); color: var(--color-text-primary);
      box-shadow: var(--shadow-lg); text-align: start; }
    .pad-icon { width: 48px; height: 48px; margin-bottom: 12px; border-radius: 50%; display: flex; align-items: center;
      justify-content: center; background: #fff3cd; color: #b45309; }
    h3 { margin: 0 0 8px; font-size: 20px; font-weight: 600; }
    .pad-body, .pad-next { margin: 0 0 12px; font-size: 14px; line-height: 1.5; color: var(--color-text-secondary); }
    .pad-list { list-style: none; margin: 0 0 12px; padding: 0; }
    .pad-list li { padding: 10px 12px; margin-bottom: 8px; border: 1px solid var(--color-border); border-radius: 8px; }
    .pad-printer { font-size: 16px; }
    .pad-printer span { margin-inline-start: 6px; font-size: 13px; color: var(--color-text-secondary); }
    .pad-since { margin-top: 4px; font-size: 13px; }
    .pad-reason { margin-top: 4px; font-size: 12px; color: var(--color-text-secondary); word-break: break-word; }
    .pad-actions { display: flex; gap: 12px; justify-content: flex-end; flex-wrap: wrap; }
    .pad-btn { min-width: 140px; min-height: 48px; padding: 12px 20px; border-radius: 8px; font-size: 15px; font-weight: 600;
      cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-surface); color: var(--color-text-primary); }
    .pad-btn-primary { border-color: #C41E3A; background: #C41E3A; color: #fff; }
  `],
})
export class PrinterAlertDialogComponent {
  private readonly api = inject(ApiService);
  private readonly net = inject(NetworkStatusService);
  private readonly state = inject(PosStateService);
  private readonly store = inject(StoreContextService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hubRouting = inject(HubRoutingService);
  private readonly offlineSession = inject(OfflineSessionGuardService);
  private readonly storage = inject(StorageService);
  private readonly realtime = inject(StoreRealtimeService);
  private readonly toast = inject(ToastService);

  alerts: PosPrinterAlert[] = [];
  hubAlerts: PosPrintHubAlert[] = [];
  private remindLaterMinutes = 15;
  private answers: PrinterAlertAnswers = this.storage.get<PrinterAlertAnswers>(StorageKey.PRINTER_ALERT_ANSWERS) ?? {};

  /**
   * Printers this terminal has listed as not responding, by alert id, for the "back online"
   * toast — dismissed or snoozed ones too: the cashier knew it was down, so is told it is back.
   */
  private shown = new Map<string, string>();

  /** PRNHUB-1 — hub alerts this terminal has listed, for the "no longer waiting" toast. */
  private shownHubs = new Set<string>();

  get visible(): boolean {
    return this.net.current() === 'online' && !!this.state.posAuth && !this.state.isLocked
      && isPosOperatorScreen(this.router.url);
  }

  /** Never while the hub serves this terminal, or with a hub-minted token the cloud cannot verify (as caller ID). */
  private get cloudReachable(): boolean {
    return !this.hubRouting.servedByHub()
      && !this.offlineSession.isOfflineMinted(this.storage.getRaw(StorageKey.POS_TOKEN));
  }

  get due(): PosPrinterAlert[] {
    return dueAlerts(this.alerts, this.answers, Date.now());
  }

  get dueHubs(): PosPrintHubAlert[] {
    return dueAlerts(this.hubAlerts, this.answers, Date.now());
  }

  /** Any shown hub alert's device is still in touch: the jobs are stuck on it, not it being off. */
  get hubDeviceOnline(): boolean {
    const now = Date.now();
    return this.dueHubs.some(h => printDeviceOnline(h, now));
  }

  constructor() {
    combineLatest([this.state.posAuth$, this.store.activeStore$]).pipe(
      tap(() => { this.alerts = []; this.hubAlerts = []; this.shown.clear(); this.shownHubs.clear(); }),
      switchMap(([auth, store]) => !auth || !store ? of(null) : merge(
        timer(0, PRINTER_ALERT_POLL_MS),
        this.realtime.event$.pipe(filter(e => HINTS.has(e.type))),
        // A reconnect counts as a hint: AppSync does not replay what was published while the socket was down.
        this.realtime.subscribed$,
      ).pipe(
        map(() => this.visible && this.cloudReachable),
        // A newer trigger restarts a read in flight, so a change is never missed.
        switchMap(read => !read ? of(null) : this.api.get<PosPrinterAlerts>('pos/printer-alerts').pipe(
          tap(res => {
            // Only pos-shell connects the store channel; the dialog also shows on cold-loaded order and settle screens.
            if (res.success && (res.data?.enabled || res.data?.hubEnabled)) this.realtime.ensureConnected(auth.tenantId, auth.storeId);
          }))),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(res => {
      // Not shown (locked, offline, another screen) or a failed read: keep what we have until the next read.
      if (!res || !res.success || !res.data) return;
      this.apply(res.data);
    });
  }

  private apply(data: PosPrinterAlerts): void {
    this.remindLaterMinutes = data.remindLaterMinutes || 15;
    const alerts = data.enabled ? data.alerts ?? [] : [];
    const live = new Set(alerts.map(a => a.id));

    // A printer this terminal showed is no longer listed while the pop-up is on: it is printing again.
    if (data.enabled) {
      for (const [id, printer] of this.shown) {
        if (!live.has(id)) this.toast.success('pos.printerAlert.backOnline', PRINTER_BACK_ONLINE_TOAST_MS, { printer });
      }
    }
    for (const id of [...this.shown.keys()]) if (!live.has(id)) this.shown.delete(id);
    for (const a of alerts) this.shown.set(a.id, a.printerName);

    // PRNHUB-1 — the hub alert going away means nothing waits any more, which is not always
    // "printed": jobs can also have expired. The toast says so either way.
    const hubAlerts = data.hubEnabled ? data.hubAlerts ?? [] : [];
    const liveHubs = new Set(hubAlerts.map(h => h.id));
    if (data.hubEnabled && [...this.shownHubs].some(id => !liveHubs.has(id))) {
      this.toast.success('pos.printerAlert.hubCleared', PRINTER_BACK_ONLINE_TOAST_MS);
    }
    this.shownHubs = liveHubs;

    this.alerts = alerts;
    this.hubAlerts = hubAlerts;
    this.saveAnswers(pruneAnswers(this.answers, [...live, ...liveHubs]));
  }

  remindLater(): void {
    const until = Date.now() + this.remindLaterMinutes * 60_000;
    this.answer('remindLater', () => ({ remindAt: until }));
  }

  dismiss(): void {
    this.answer('dismiss', () => ({ dismissed: true }));
  }

  private answer(action: 'dismiss' | 'remindLater', make: () => PrinterAlertAnswer): void {
    const now = Date.now();
    const due = dueAlerts(this.alerts, this.answers, now);
    const dueHubs = dueAlerts(this.hubAlerts, this.answers, now);
    if (due.length === 0 && dueHubs.length === 0) return;
    const next = { ...this.answers };
    for (const a of [...due, ...dueHubs]) next[a.id] = make();
    this.saveAnswers(next);
    // For the Printer health history only: the answer already holds here if this fails.
    const paths = [
      ...due.map(a => `pos/printer-alerts/${a.id}/acknowledge`),
      ...dueHubs.map(h => `pos/printer-alerts/hub/${h.id}/acknowledge`),
    ];
    for (const path of paths) {
      this.api.post<unknown>(path, { action })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  private saveAnswers(answers: PrinterAlertAnswers): void {
    this.answers = answers;
    this.storage.set(StorageKey.PRINTER_ALERT_ANSWERS, answers);
  }
}
